import { zonedDateKey, type Microsoft365AdminDto, type Microsoft365SettingsBody, type Microsoft365TestResultDto } from '@dbrain/shared';
import { Alert, Button, ConfirmDialog, Icons, Skeleton, Switch } from '@dbrain/ui';
import { humanTime } from '@dbrain/app-core/format';
import { useId, useState, type ReactNode } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { useCompanyZone } from '../../../api/hooks.js';
import { useMicrosoft365Admin, useMicrosoft365Settings, useMicrosoft365Test, useRemoveMicrosoft365 } from '../../../api/microsoft365-admin.js';
import { ErrorAlert } from '../../ErrorAlert.js';
import { MicrosoftIcon } from '../MicrosoftIcon.js';
import { AudienceDialog } from './AudienceDialog.js';
import {
  allowedTeams,
  consentOf,
  dateLabel,
  documentsOn,
  featureChange,
  featureRows,
  maskId,
  nameList,
  secretExpiry,
  SETUP_PATH,
  setupStatus,
  teamsTurnedOff,
  testCodeOf,
  writesOn,
  type FeatureRowView,
} from './model.js';
import { BuiltinTile, StatusPill } from './parts.js';

/**
 * Yönetim › Entegrasyonlar › Microsoft 365 (MsYonetimEntegrasyon): the state with how many people are connected and
 * the firm's switch, "Neler açık" (reading and sending mail, reading the calendar and creating meetings, documents
 * from OneDrive and SharePoint; part 3), "Kim kullanabilir", the Microsoft connection (sign-in method, masked app id,
 * the secret's end date, the last test, the account match) and removing the setup. Switches save at once; each
 * change is audited.
 */
export function Microsoft365Summary() {
  const { t } = useTranslation();
  const admin = useMicrosoft365Admin();
  const settings = useMicrosoft365Settings();
  const remove = useRemoveMicrosoft365();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  if (admin.isPending) return <Skeleton block />;
  if (admin.error) return <ErrorAlert error={admin.error} />;
  const dto = admin.data;
  const status = setupStatus(dto);
  if (status === 'not_set_up') return <Navigate to={SETUP_PATH} replace />;
  const consent = consentOf(params.get('consent'));
  return (
    <div className="m365a-admin m365a-summary" data-testid="m365-summary" data-status={status}>
      {consent ? (
        <Alert
          tone={consent === 'granted' ? 'success' : 'warning'}
          title={t(consent === 'granted' ? 'microsoft365.admin.summary.consentGranted' : 'microsoft365.admin.summary.consentDenied')}
          action={
            <span className="row">
              {consent === 'denied' ? (
                <Link className="ui-button ui-button--secondary ui-button--sm" to={`${SETUP_PATH}?step=3`}>
                  {t('microsoft365.admin.summary.consentRetry')}
                </Link>
              ) : null}
              <Button size="sm" variant="ghost" onClick={() => setParams({}, { replace: true })}>
                {t('microsoft365.admin.summary.close')}
              </Button>
            </span>
          }
          data-testid="m365-consent-result"
        >
          {t(consent === 'granted' ? 'microsoft365.admin.summary.consentGrantedText' : 'microsoft365.admin.summary.consentDeniedText')}
        </Alert>
      ) : null}
      <Head dto={dto} busy={settings.isPending} onFirm={(enabled) => settings.mutate({ enabled })} />
      <ErrorAlert error={settings.error} />
      {dto.configured ? null : (
        <Alert
          tone="warning"
          title={t('microsoft365.admin.summary.incompleteTitle')}
          action={
            <Link className="ui-button ui-button--secondary ui-button--sm" to={`${SETUP_PATH}?step=2`} data-testid="m365-resume">
              {t('microsoft365.admin.overview.resume')}
            </Link>
          }
          data-testid="m365-incomplete"
        >
          {t(dto.signInMethod === 'auth_code' && !dto.hasSecret ? 'microsoft365.admin.summary.incompleteSecret' : 'microsoft365.admin.summary.incomplete')}
        </Alert>
      )}
      <Features dto={dto} busy={settings.isPending} onChange={(body) => settings.mutate(body)} />
      <Audience dto={dto} onEdit={() => setEditing(true)} />
      <Connection dto={dto} busy={settings.isPending} onMatchAccount={(matchAccount) => settings.mutate({ matchAccount })} />
      <section className="admin-card m365a-danger" data-testid="m365-remove">
        <p>{t('microsoft365.admin.summary.remove.hint')}</p>
        <Button variant="danger" onClick={() => setRemoving(true)} data-testid="m365-remove-open">
          {t('microsoft365.admin.summary.remove.button')}
        </Button>
      </section>
      {editing ? <AudienceDialog dto={dto} onClose={() => setEditing(false)} /> : null}
      {removing ? (
        <ConfirmDialog
          title={t('microsoft365.admin.summary.remove.confirmTitle')}
          confirmLabel={t('microsoft365.admin.summary.remove.button')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate(undefined, { onSuccess: () => void navigate('/admin/integrations', { replace: true }) })}
          onClose={() => setRemoving(false)}
          data-testid="m365-remove-confirm"
        >
          {t('microsoft365.admin.summary.remove.confirmBody', { count: dto.connectedCount })}
        </ConfirmDialog>
      ) : null}
    </div>
  );
}

/** "Microsoft 365 · Çalışıyor · Şu an 31 kişi bağlı" and "Firmada açık" (off: no one can use it, accounts are kept). */
function Head({ dto, busy, onFirm }: { dto: Microsoft365AdminDto; busy: boolean; onFirm: (enabled: boolean) => void }) {
  const { t } = useTranslation();
  const hintId = useId();
  return (
    <section className="admin-card m365a-head" data-testid="m365-summary-head">
      <BuiltinTile large />
      <div className="m365a-head__text">
        <div className="m365a-head__title">
          <h2>{t('microsoft365.admin.name')}</h2>
          <StatusPill status={setupStatus(dto)} />
        </div>
        <p className="m365a-head__lead" data-testid="m365-connected">
          <Trans i18nKey="microsoft365.admin.summary.lead" count={dto.connectedCount} components={{ b: <strong /> }} />
        </p>
        {dto.enabled ? null : (
          <p id={hintId} className="m365a-head__hint">
            {t('microsoft365.admin.summary.firmOffHint')}
          </p>
        )}
      </div>
      <label className="m365a-head__switch">
        <span>{t('microsoft365.admin.summary.firm')}</span>
        <Switch
          checked={dto.enabled}
          disabled={!dto.configured || busy}
          aria-describedby={dto.enabled ? undefined : hintId}
          onChange={(e) => onFirm(e.target.checked)}
          data-testid="m365-firm-switch"
        />
      </label>
    </section>
  );
}

/**
 * "Neler açık" (MsYonetimEntegrasyon, part 3): six rows in the mockup's order, each with its product's icon beside its
 * full name. A write row needs its read row on (disabled and saying so meanwhile; turning the read row off turns it
 * off too); a document row reminds of Entra's permission once on (no mockup). The lock note says how writes ask.
 */
function Features({ dto, busy, onChange }: { dto: Microsoft365AdminDto; busy: boolean; onChange: (body: Microsoft365SettingsBody) => void }) {
  const { t } = useTranslation();
  return (
    <section className="admin-card m365a-section" data-testid="m365-features">
      <h2 className="m365a-section__title">{t('microsoft365.admin.summary.features.title')}</h2>
      <ul className="m365a-rows">
        {featureRows(dto.features).map((row) => (
          <FeatureRow
            key={row.feature}
            row={row}
            disabled={busy}
            onChange={(on) => {
              const body = featureChange(row.feature, on, dto.features);
              if (body) onChange(body);
            }}
          />
        ))}
      </ul>
      <p className="m365a-section__note" data-testid="m365-features-note">
        <Icons.lock size={14} />
        <span>{t(writesOn(dto.features) ? 'microsoft365.admin.summary.features.note' : 'microsoft365.admin.summary.features.readOnly')}</span>
      </p>
    </section>
  );
}

/** Microsoft's own icon before each product's name: OneDrive's and SharePoint's for their rows, Outlook's for mail and the calendar. */
function FeatureIcon({ feature }: { feature: FeatureRowView['feature'] }) {
  return <MicrosoftIcon product={feature === 'onedrive_import' ? 'onedrive' : feature === 'sharepoint_import' ? 'sharepoint' : 'outlook'} size={20} />;
}

function FeatureRow({ row, disabled, onChange }: { row: FeatureRowView; disabled: boolean; onChange: (on: boolean) => void }) {
  const { t } = useTranslation();
  const id = useId();
  const hint = row.needs ? t(`microsoft365.admin.summary.features.needs.${row.needs}`) : t(`microsoft365.admin.summary.features.${row.key}.hint`);
  return (
    <li className="m365a-row" data-testid={`m365-feature-${row.testId}`} data-on={row.on ? 'true' : 'false'}>
      <FeatureIcon feature={row.feature} />
      <span className="m365a-row__text">
        <label htmlFor={id} className="m365a-row__title">
          {t(`microsoft365.admin.summary.features.${row.key}.title`)}
        </label>
        <span id={`${id}-hint`} className={row.needs ? 'm365a-row__hint m365a-row__hint--blocked' : 'm365a-row__hint'} data-testid={`m365-feature-${row.testId}-hint`}>
          {hint}
        </span>
        {row.kind === 'documents' && row.on ? (
          <span className="m365a-row__warning" role="note" data-testid={`m365-feature-${row.testId}-entra`}>
            <Icons.info size={14} />
            {t('microsoft365.admin.summary.features.entra')}
          </span>
        ) : null}
      </span>
      <Switch
        id={id}
        aria-describedby={`${id}-hint`}
        checked={row.on}
        disabled={disabled || row.needs !== null}
        onChange={(e) => onChange(e.target.checked)}
        data-testid={`m365-feature-${row.testId}-switch`}
      />
    </li>
  );
}

/** "Kim kullanabilir": everyone or the chosen teams, and the teams whose admin turned it off. */
function Audience({ dto, onEdit }: { dto: Microsoft365AdminDto; onEdit: () => void }) {
  const { t, i18n } = useTranslation();
  const more = (count: number) => t('microsoft365.admin.summary.audience.moreTeams', { count });
  const chosen = allowedTeams(dto.teams).map((team) => team.name);
  const who =
    dto.availability === 'everyone'
      ? t('microsoft365.admin.summary.audience.everyone')
      : chosen.length > 0
        ? t('microsoft365.admin.summary.audience.teams', { teams: nameList(chosen, i18n.language, more) })
        : t('microsoft365.admin.summary.audience.noTeams');
  const off = teamsTurnedOff(dto.teams);
  const hint = [t('microsoft365.admin.summary.audience.hint'), off.length > 0 ? t('microsoft365.admin.summary.audience.turnedOff', { count: off.length, teams: nameList(off, i18n.language, more) }) : null]
    .filter(Boolean)
    .join(' · ');
  return (
    <section className="admin-card m365a-section" data-testid="m365-audience">
      <div className="m365a-section__head">
        <h2 className="m365a-section__title">{t('microsoft365.admin.summary.audience.title')}</h2>
        <Button size="sm" variant="secondary" icon={<Icons.edit size={14} />} onClick={onEdit} data-testid="m365-audience-edit">
          {t('microsoft365.admin.summary.audience.edit')}
        </Button>
      </div>
      <div className="m365a-row m365a-row--text">
        <span className="m365a-row__text">
          <strong className="m365a-row__title" data-testid="m365-audience-who">
            {who}
          </strong>
          <span className="m365a-row__hint" data-testid="m365-audience-hint">
            {hint}
          </span>
        </span>
      </div>
      {documentsOn(dto.features) ? (
        <div className="m365a-row m365a-row--text" data-testid="m365-audience-documents">
          <span className="m365a-row__text">
            <strong className="m365a-row__title">{t('microsoft365.admin.summary.audience.documents.title')}</strong>
            <span className="m365a-row__hint">{t('microsoft365.admin.summary.audience.documents.hint')}</span>
          </span>
        </div>
      ) : null}
    </section>
  );
}

/**
 * "Microsoft bağlantısı": the sign-in method, the masked app id, the secret's end date, the last test and whether a
 * person may connect only the Microsoft account with their own Oppy e-mail address.
 */
function Connection({ dto, busy, onMatchAccount }: { dto: Microsoft365AdminDto; busy: boolean; onMatchAccount: (on: boolean) => void }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  const test = useMicrosoft365Test();
  // the company's today (D-119)
  const expiry = secretExpiry(dto.secretExpiresOn, zonedDateKey(new Date(), zone));
  const facts: { key: string; label: string; value: ReactNode; tone?: 'warning' | 'danger' }[] = [
    { key: 'method', label: t('microsoft365.admin.summary.connection.method'), value: <strong>{t(`microsoft365.admin.setup.details.methods.${dto.signInMethod}.title`)}</strong> },
    { key: 'client', label: t('microsoft365.admin.summary.connection.clientId'), value: <code className="m365a-mono">{maskId(dto.clientId) ?? '—'}</code> },
  ];
  if (dto.hasSecret || dto.signInMethod === 'auth_code') {
    const date = dto.secretExpiresOn ? dateLabel(dto.secretExpiresOn, i18n.language) : null;
    facts.push({
      key: 'expires',
      label: t('microsoft365.admin.summary.connection.expires'),
      value:
        !date || expiry.kind === 'none' ? (
          <span className="muted">{t('microsoft365.admin.summary.connection.expiresNone')}</span>
        ) : expiry.kind === 'expired' ? (
          <strong>{t('microsoft365.admin.summary.connection.expired', { date })}</strong>
        ) : expiry.kind === 'soon' ? (
          <strong>{expiry.days === 0 ? t('microsoft365.admin.summary.connection.expiresToday', { date }) : t('microsoft365.admin.summary.connection.expiresSoon', { date, count: expiry.days })}</strong>
        ) : (
          <strong>{date}</strong>
        ),
      ...(expiry.kind === 'expired' ? { tone: 'danger' as const } : expiry.kind === 'soon' ? { tone: 'warning' as const } : {}),
    });
  } else {
    facts.push({ key: 'secret', label: t('microsoft365.admin.summary.connection.noSecret'), value: <span className="muted">{t('microsoft365.admin.summary.connection.noSecretValue')}</span> });
  }
  return (
    <section className="admin-card m365a-section" data-testid="m365-connection">
      <div className="m365a-section__head">
        <h2 className="m365a-section__title">{t('microsoft365.admin.summary.connection.title')}</h2>
        <Link className="ui-button ui-button--secondary ui-button--sm" to={`${SETUP_PATH}?step=2`} data-testid="m365-open-setup">
          <Icons.edit size={14} />
          {t('microsoft365.admin.summary.connection.open')}
        </Link>
      </div>
      <dl className="m365a-facts">
        {facts.map((fact) => (
          <div key={fact.key} className={`m365a-facts__item${fact.tone ? ` m365a-facts__item--${fact.tone}` : ''}`} data-testid={`m365-fact-${fact.key}`}>
            <dt>{fact.label}:</dt>
            <dd>{fact.value}</dd>
          </div>
        ))}
      </dl>
      <div className="m365a-last-test">
        <LastTest result={test.data ?? dto.lastTest} />
        <Button size="sm" variant="secondary" busy={test.isPending} onClick={() => test.mutate()} data-testid="m365-retest">
          {t('microsoft365.admin.summary.connection.retest')}
        </Button>
      </div>
      <ErrorAlert error={test.error} />
      {/* an API from before the switch sends no value: the server's default, on */}
      <MatchAccountRow on={dto.matchAccount !== false} disabled={busy} onChange={onMatchAccount} />
    </section>
  );
}

/**
 * D-194 (security review; no mockup, a row like "Neler açık"): the Microsoft account must have the person's Oppy
 * e-mail address, so no one connects a colleague's mailbox, for example by having them type a sign-in code (Kodla).
 * On by default; off only for firms whose people have other addresses at Microsoft.
 */
function MatchAccountRow({ on, disabled, onChange }: { on: boolean; disabled: boolean; onChange: (on: boolean) => void }) {
  const { t } = useTranslation();
  const id = useId();
  return (
    <div className="m365a-row m365a-match" data-testid="m365-match-account">
      <Icons.shield size={18} />
      <span className="m365a-row__text">
        <label htmlFor={id} className="m365a-row__title">
          {t('microsoft365.admin.summary.connection.matchAccount.title')}
        </label>
        <span id={`${id}-hint`} className="m365a-row__hint">
          {t('microsoft365.admin.summary.connection.matchAccount.hint')}
        </span>
      </span>
      <Switch id={id} aria-describedby={`${id}-hint`} checked={on} disabled={disabled} onChange={(e) => onChange(e.target.checked)} data-testid="m365-match-account-switch" />
    </div>
  );
}

/** "Son test başarılı · bugün 11:42", or why it failed in plain words. */
function LastTest({ result }: { result: Microsoft365TestResultDto | null }) {
  const { t, i18n } = useTranslation();
  const zone = useCompanyZone();
  if (!result) {
    return (
      <span className="m365a-last-test__text muted" role="status" data-testid="m365-last-test" data-result="never">
        {t('microsoft365.admin.summary.connection.never')}
      </span>
    );
  }
  const when = Number.isNaN(Date.parse(result.testedAt)) ? '—' : humanTime(result.testedAt, i18n.language, t, true, zone);
  return (
    <span className={`m365a-last-test__text m365a-last-test__text--${result.ok ? 'ok' : 'failed'}`} role="status" data-testid="m365-last-test" data-result={result.ok ? 'ok' : (result.code ?? 'unexpected')}>
      {result.ok ? <Icons.check size={14} /> : <Icons.error size={14} />}
      {result.ok
        ? t('microsoft365.admin.summary.connection.lastOk', { when })
        : t('microsoft365.admin.summary.connection.lastFailed', { when, reason: t(`microsoft365.admin.testCodes.${testCodeOf(result.code)}.title`) })}
    </span>
  );
}
